import { useQuery } from '@tanstack/react-query';

import adminModule from '../api/modules/admin.module';
import { useAuth } from './useAuth';

/**
 * Whether Sleevr can ship right now — and, just as importantly, whether we are
 * in a position to know.
 *
 * `SystemSetting.sell_in_person` is an admin switch. TRUE adds in-person
 * handover as a choice at checkout alongside "Ship to me"; FALSE is the model
 * default and the ordinary shipping-only flow. Either way Sleevr ships, so
 * once the setting is known the answer is 'shipping'. ('in_person_only' was
 * the answer while shipping sat behind a "Coming soon" tile; it is gone.)
 *
 * WHY THIS RETURNS THREE STATES AND NOT A BOOLEAN
 * -----------------------------------------------
 * `GET /api/dashboard/system-setting` is `IsAuthenticated`. The two loudest
 * shipping promises in the app are on surfaces with no session to authenticate
 * with — the card detail buy box and the auth splash — so on those the honest
 * answer is not "shipping works", it is "we cannot tell from here".
 *
 * A boolean would have to pick a side, and every existing caller of this
 * endpoint picks the optimistic one (`{ sell_in_person: false }` on failure).
 * That is survivable for a fulfilment PICKER, which only ever offers a choice.
 * It is not survivable for a PROMISE: defaulting to "yes we ship" is exactly
 * how the card page came to advertise nationwide delivery to a buyer whose
 * only checkout option was meeting the seller in a car park.
 *
 * So: 'shipping' is positive knowledge. 'unknown' is not
 * a loading state to paper over — it is the answer, and a caller making a
 * claim must say nothing rather than guess. See `TrustRows`.
 *
 * The query key is shared with the existing inline copies in the seller
 * dashboard (`EditListingModal`, `OrderDetailsDrawer`,
 * `PricingQuantityStep`), so mounting this alongside them costs no extra
 * request. Those three want the platform fee rather than the fulfilment mode
 * and are left where they are.
 */
export function useFulfilmentMode() {
  const { isAuthenticated } = useAuth();

  const { data, isSuccess } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const result = await adminModule.getSystemSettings();
      if (result.success) {
        return result.data;
      }
      /* Deliberately NOT throwing: the seller-dashboard copies of this query
         swallow a failure into `{ sell_in_person: false }` and share this
         cache key, so throwing here would change what THEY see. The honesty
         this hook adds is in `isAuthenticated` — the surfaces that cannot ask
         get 'unknown' — not in second-guessing a request that did run. */
      return { sell_in_person: false, platform_fee: 5 };
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });

  if (!isAuthenticated || !isSuccess || !data) return 'unknown';
  return 'shipping';
}

export default useFulfilmentMode;
